// coachActions.js — write helpers the AI Coach uses to log into the user's
// Hayah data straight from chat. Standalone localStorage writers that mirror
// CalorieCounter's addFoodEntry entry shape, so the Coach can act without
// reaching into that component. Every writer dispatches a storage event so
// the Calories tab / Today dashboard re-render immediately.

const MEAL_SLOTS = ["meal", "drinks", "supplements", "whey", "snacks"];

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function genId() { return Math.random().toString(36).slice(2) + Date.now().toString(36); }
function round1(v) { return Math.round((Number(v) || 0) * 10) / 10; }

function loadLog() {
  try { return JSON.parse(localStorage.getItem("calorieLog") || "{}"); } catch { return {}; }
}
function saveLog(log) {
  try {
    localStorage.setItem("calorieLog", JSON.stringify(log));
    window.dispatchEvent(new StorageEvent("storage", { key: "calorieLog" }));
  } catch {}
}

// Build a calorieLog meal entry from an AI-described food item. The AI gives
// per-serving totals, so macros are written as-is (no scaling).
function toEntry(it) {
  return {
    id: genId(),
    name: String(it.name || "Food"),
    brand: it.brand || null,
    calories: round1(it.calories),
    protein:  round1(it.protein),
    carbs:    round1(it.carbs),
    fat:      round1(it.fat),
    fiber:    round1(it.fiber || 0),
    sugar:    round1(it.sugar || 0),
    sodium:   Math.round(it.sodium || 0),
    servingG: Number(it.servingG) || 100,
    servingLabel: it.servingLabel || "1 serving",
    source: "ai_coach",
    halal: it.halal != null ? it.halal : null,
    loggedAt: new Date().toISOString(),
  };
}

// Log one or more food items to today's diary under `slot`. Returns
// { count, kcal, slot, ids } — ids let the chat offer an Undo.
export function logFood(slot, items) {
  const routed = MEAL_SLOTS.includes(slot) ? slot : "meal";
  const entries = (items || []).filter(it => it && it.name).map(toEntry);
  if (!entries.length) return { count: 0, kcal: 0, slot: routed, ids: [] };
  const dt = todayKey();
  const log = loadLog();
  const prev = log[dt] || { meals: { meal: [] }, water: 0, weight: null };
  const meals = { ...(prev.meals || {}) };
  meals[routed] = [...(meals[routed] || []), ...entries];
  log[dt] = { ...prev, meals };
  saveLog(log);
  return {
    count: entries.length,
    kcal: Math.round(entries.reduce((s, e) => s + (e.calories || 0), 0)),
    slot: routed,
    ids: entries.map(e => e.id),
  };
}

// Undo a logFood write — remove the given entry ids from today's slot.
export function removeFoodEntries(slot, ids) {
  const dt = todayKey();
  const log = loadLog();
  const prev = log[dt];
  if (!prev || !prev.meals || !Array.isArray(prev.meals[slot])) return;
  const idset = new Set(ids || []);
  log[dt] = { ...prev, meals: { ...prev.meals, [slot]: prev.meals[slot].filter(e => !idset.has(e.id)) } };
  saveLog(log);
}

// Water is stored in CUPS (1 cup = 250 ml). Accepts ml and/or cups.
export function logWater({ ml, cups } = {}) {
  const addCups = (Number(cups) || 0) + (Number(ml) || 0) / 250;
  if (addCups <= 0) return { cups: 0 };
  const dt = todayKey();
  const log = loadLog();
  const prev = log[dt] || { meals: { meal: [] }, water: 0 };
  const next = Math.round(((prev.water || 0) + addCups) * 10) / 10;
  log[dt] = { ...prev, water: next };
  saveLog(log);
  return { cups: next };
}

// Append to the manual weight log. Writes ts + date + kg so every reader
// (weeklyChecks weigh-in, weight trend) picks it up.
export function logWeight(kg) {
  const k = Number(kg);
  if (!(k > 0) || k > 400) return null;
  const dt = todayKey();
  let arr = [];
  try { arr = JSON.parse(localStorage.getItem("calorieWeightLog") || "[]"); } catch {}
  if (!Array.isArray(arr)) arr = [];
  const next = [...arr.filter(w => w.date !== dt), { ts: Date.now(), date: dt, kg: k }]
    .sort((a, b) => (a.ts || 0) - (b.ts || 0));
  try {
    localStorage.setItem("calorieWeightLog", JSON.stringify(next));
    window.dispatchEvent(new StorageEvent("storage", { key: "calorieWeightLog" }));
  } catch {}
  return { kg: k };
}

// Log the weekly Mounjaro dose — written as a supplements entry whose name
// matches getMounjaroDay's /mounjaro|tirzepatide/i scan, so the GLP-1 reminder
// cycle resets automatically.
export function logMounjaro() {
  logFood("supplements", [{
    name: "Mounjaro (tirzepatide) weekly dose",
    calories: 0, protein: 0, carbs: 0, fat: 0,
    servingLabel: "1 injection", halal: true,
  }]);
  return { ok: true };
}

// Save a food to Quick Add (favourites), mirroring CalorieCounter's fav shape
// so it shows up as a one-tap tile. Deduped by name.
export function addFavoriteFood(it) {
  if (!it || !it.name) return { ok: false };
  let favs = [];
  try { favs = JSON.parse(localStorage.getItem("caloriesFavorites") || "[]"); } catch {}
  if (!Array.isArray(favs)) favs = [];
  if (favs.some(f => f && f.type === "food" && f.name === it.name)) return { ok: true, dup: true };
  const baseG = Number(it.servingG) || 100;
  const fav = {
    id: "fav_" + genId(), type: "food", name: it.name,
    food: {
      name: it.name, brand: it.brand || null,
      calories: round1(it.calories), protein: round1(it.protein), carbs: round1(it.carbs), fat: round1(it.fat),
      fiber: round1(it.fiber || 0), sugar: round1(it.sugar || 0), sodium: Math.round(it.sodium || 0),
      servingG: baseG, servingLabel: it.servingLabel || `${baseG}g`,
      source: "ai_coach", halal: it.halal != null ? it.halal : null,
    },
    defaultServingG: baseG, defaultServingLabel: it.servingLabel || `${baseG}g`,
    usageCount: 0, lastUsed: todayKey(),
  };
  try {
    localStorage.setItem("caloriesFavorites", JSON.stringify([fav, ...favs]));
    window.dispatchEvent(new StorageEvent("storage", { key: "caloriesFavorites" }));
  } catch {}
  return { ok: true };
}

// ── LOCAL FOOD RESOLVER ───────────────────────────────────────────────────
// "Log <food>" should first try the user's OWN data — recent diary entries,
// Quick-Add favourites, and their saved foods — and only fall back to the AI
// estimate when nothing matches. That's instant, free, and uses the exact
// macros the user trusts. resolveLocalLog(text) returns grouped items ready
// for logFood(), or null when it can't confidently resolve every item (in
// which case the caller should hand off to the AI extractor).

function _norm(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\b(a|an|the|some|my|of|with|fresh|raw)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
function _tokens(s) {
  return _norm(s).split(" ").filter(Boolean)
    .map(w => (w.length > 3 && w.endsWith("s")) ? w.slice(0, -1) : w); // crude singular
}
function _sameTokens(a, b) {
  const A = _tokens(a), B = _tokens(b);
  if (!A.length || A.length !== B.length) return false;
  const bs = new Set(B);
  return A.every(t => bs.has(t));
}
// True when `candName`'s leading whole tokens equal the query's tokens — so a
// short query ("cappuccino") matches a stored food whose name carries a brand
// / detail suffix ("Cappuccino · Woolies Lactose-free Low Fat Milk"). Matching
// is anchored at the FIRST token, so trailing words ("…Low Fat Milk") never
// match a query like "milk".
function _startsWithTokens(candName, queryStr) {
  const C = _tokens(candName), Q = _tokens(queryStr);
  if (!Q.length || Q.length > C.length) return false;
  for (let i = 0; i < Q.length; i++) if (C[i] !== Q[i]) return false;
  return true;
}
function _inferSlot(name) {
  const n = String(name || "").toLowerCase();
  if (/\b(coffee|cappuccino|latte|espresso|americano|macchiato|flat white|tea|chai|juice|smoothie|coke|cola|soda|soft drink|lemonade|milkshake|drink)\b/.test(n)) return "drinks";
  if (/\b(whey|protein shake|protein powder|casein|mass gainer)\b/.test(n)) return "whey";
  if (/\b(creatine|vitamin|omega|supplement|pre.?workout|bcaa|magnesium|zinc|fish oil|electrolyte)\b/.test(n)) return "supplements";
  if (/\b(chocolate|candy|sweet|sweets|crisp|crisps|chips|biscuit|cookie|ice cream|pastille|pastilles|cake|donut|doughnut|gummy|gummies)\b/.test(n)) return "snacks";
  return "meal";
}
const _NUMWORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };
function _parseQty(phrase) {
  const m = String(phrase || "").match(/^\s*(\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten)\s+(.*)$/i);
  if (m) {
    const q = _NUMWORDS[m[1].toLowerCase()] != null ? _NUMWORDS[m[1].toLowerCase()] : parseFloat(m[1]);
    if (Number.isFinite(q) && q > 0 && q <= 20 && m[2].trim()) return { qty: q, rest: m[2].trim() };
  }
  return { qty: 1, rest: String(phrase || "").trim() };
}

// Build the candidate pool from the user's own data, newest/most-trusted first.
// Only foods with a real (>0) calorie value are eligible, so a stale 0-kcal
// entry never gets re-logged as a wrong match.
function _candidateFoods() {
  const list = [];
  const log = loadLog();
  const entries = [];
  for (const day of Object.values(log || {})) {
    const meals = (day && day.meals) || {};
    for (const slot of Object.keys(meals)) {
      for (const e of (meals[slot] || [])) entries.push({ e, slot });
    }
  }
  entries.sort((a, b) => new Date(b.e.loggedAt || 0) - new Date(a.e.loggedAt || 0));
  const seen = new Set();
  for (const { e, slot } of entries) {
    const key = _norm(e.name);
    if (!key || seen.has(key) || !(Number(e.calories) > 0)) continue;
    seen.add(key);
    list.push({ ...e, slot, _src: "your recents" });
    if (list.length >= 60) break;
  }
  try {
    const favs = JSON.parse(localStorage.getItem("caloriesFavorites") || "[]");
    for (const f of favs) {
      const fd = f && f.type === "food" ? f.food : null;
      if (fd && fd.name && Number(fd.calories) > 0) {
        const key = _norm(fd.name);
        if (seen.has(key)) continue;
        seen.add(key);
        list.push({ ...fd, slot: _inferSlot(fd.name), _src: "your Quick Add" });
      }
    }
  } catch {}
  try {
    const uf = JSON.parse(localStorage.getItem("ghidhaUserFoods") || "[]");
    for (const fd of uf) {
      if (fd && fd.name && Number(fd.calories) > 0) {
        const key = _norm(fd.name);
        if (seen.has(key)) continue;
        seen.add(key);
        list.push({ ...fd, slot: _inferSlot(fd.name), _src: "your saved foods" });
      }
    }
  } catch {}
  return list;
}

// Try to resolve a "log X" message entirely from the user's own data.
// Returns { groups: { slot: items[] }, names: [...], source } or null.
export function resolveLocalLog(text) {
  const raw = String(text || "").trim();
  if (!raw) return null;
  let body = raw
    .replace(/[.?!]+$/g, "")
    .replace(/^\s*(please\s+)?(can you\s+|could you\s+|pls\s+|plz\s+)?(log|record|track|add|note)\s+/i, "")
    .replace(/^\s*i\s+(just\s+)?(had|ate|drank|took|have eaten|have had)\s+/i, "")
    .trim();
  if (!body) return null;
  // Non-food log intents → let the AI extractor handle them.
  if (/\b(water|weight|kg|kgs|kilo|kilos|ml|litre|liter|mounjaro|glp|ozempic|cup|cups)\b/i.test(body)) return null;
  const phrases = body.split(/\s*(?:,|\band\b|&|\+)\s*/i).map(s => s.trim()).filter(Boolean);
  if (!phrases.length || phrases.length > 4) return null;

  const cands = _candidateFoods();
  if (!cands.length) return null;

  const resolved = [];
  for (const p of phrases) {
    const { qty, rest } = _parseQty(p);
    const q = _norm(rest);
    if (!q) return null;
    const hit = cands.find(c => _norm(c.name) === q)            // exact name
             || cands.find(c => _sameTokens(c.name, rest))      // same words, any order/plural
             || cands.find(c => _startsWithTokens(c.name, rest)); // query is the leading name (brand suffix ok)
    if (!hit) return null; // any miss → whole message goes to the AI
    resolved.push({ hit, qty });
  }

  const groups = {};
  const names = [];
  const srcs = new Set();
  for (const { hit, qty } of resolved) {
    const item = {
      name: hit.name,
      brand: hit.brand || null,
      calories: round1((hit.calories || 0) * qty),
      protein: round1((hit.protein || 0) * qty),
      carbs: round1((hit.carbs || 0) * qty),
      fat: round1((hit.fat || 0) * qty),
      fiber: round1((hit.fiber || 0) * qty),
      sugar: round1((hit.sugar || 0) * qty),
      sodium: Math.round((hit.sodium || 0) * qty),
      servingG: (Number(hit.servingG) || 100) * qty,
      servingLabel: qty > 1 ? `${qty} × ${hit.servingLabel || "1 serving"}` : (hit.servingLabel || "1 serving"),
      halal: hit.halal != null ? hit.halal : null,
    };
    const slot = hit.slot || "meal";
    (groups[slot] = groups[slot] || []).push(item);
    names.push(qty > 1 ? `${qty} ${hit.name}` : hit.name);
    srcs.add(hit._src);
  }
  return { groups, names, source: srcs.size === 1 ? [...srcs][0] : "your foods" };
}
